import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '../../../components/ui/alert';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import type { CommitteePaperItem } from '@/hooks/use-committee-papers';
import { useAvailableEvaluators, useReassignEvaluators, useReassignImpact } from '@/hooks/use-committee-papers';

interface ReassignEvaluatorsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventId: string;
  paper: CommitteePaperItem | null;
}

export function ReassignEvaluatorsDialog({ open, onOpenChange, eventId, paper }: ReassignEvaluatorsDialogProps) {
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [impactConfirmed, setImpactConfirmed] = useState(false);

  const { data: evaluators = [], isLoading } = useAvailableEvaluators(eventId, paper?.id || '', open);
  const impactMutation = useReassignImpact();
  const reassignMutation = useReassignEvaluators(eventId);

  useEffect(() => {
    if (!open) {
      setSearch('');
      setImpactConfirmed(false);
      setSelectedIds([]);
      return;
    }

    const preselected = evaluators.filter((item) => item.selected).map((item) => item.id);
    setSelectedIds(preselected);
  }, [open, evaluators]);

  useEffect(() => {
    if (!open || !paper || selectedIds.length === 0) return;
    impactMutation.mutate({
      paperId: paper.id,
      evaluatorIds: selectedIds,
    });
  }, [open, paper, selectedIds]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return evaluators;
    return evaluators.filter((item) =>
      `${item.name} ${item.email} ${item.institution || ''}`.toLowerCase().includes(q),
    );
  }, [evaluators, search]);

  const impact = impactMutation.data?.data;
  const requiresImpactConfirmation = Boolean(impact?.requiresConfirmation);

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) return prev.filter((item) => item !== id);
      if (prev.length >= 3) return prev;
      return [...prev, id];
    });
  };

  const handleSubmit = async () => {
    if (!paper) return;
    if (selectedIds.length === 0 || selectedIds.length > 3) {
      toast.error('Debe seleccionar entre 1 y 3 evaluadores.');
      return;
    }

    if (requiresImpactConfirmation && !impactConfirmed) {
      toast.error('Debe confirmar que entiende el impacto de la reasignacion.');
      return;
    }

    try {
      await reassignMutation.mutateAsync({
        paperId: paper.id,
        evaluatorIds: selectedIds,
        confirmImpact: requiresImpactConfirmation ? impactConfirmed : false,
      });
      toast.success('Reasignacion aplicada correctamente.');
      onOpenChange(false);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'No se pudo completar la reasignacion.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[90vh] max-h-[760px] flex-col gap-0 overflow-hidden p-0 sm:max-w-3xl">
        <DialogHeader className="shrink-0 px-6 pb-4 pt-6">
          <DialogTitle className="flex items-center gap-2 text-4xl font-bold text-[#1e3a5f]">
            <AlertTriangle className="h-6 w-6 text-amber-600" />
            Reasignar Evaluadores
          </DialogTitle>
          <DialogDescription>
            {paper ? `${paper.folio} - ${paper.title}` : 'Ajuste de evaluadores'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden px-6 py-2">
          <Alert className="border-amber-300 bg-amber-50 text-amber-900">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Cambiar evaluadores puede afectar evaluaciones en curso.</AlertTitle>
            <AlertDescription>
              Revise el impacto antes de confirmar la reasignacion.
            </AlertDescription>
          </Alert>

          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="pl-9"
              placeholder="Buscar evaluador..."
            />
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto rounded-lg border border-gray-200">
            <div className="space-y-2 p-2">
              {isLoading ? (
                <div className="flex items-center justify-center py-8 text-gray-500">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Cargando evaluadores...
                </div>
              ) : (
                filtered.map((item) => {
                  const checked = selectedIds.includes(item.id);
                  const reachedMax = selectedIds.length >= 3 && !checked;
                  return (
                    <button
                      type="button"
                      key={item.id}
                      className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left transition ${
                        checked ? 'border-[#c9a227] bg-yellow-50' : 'border-gray-200 hover:border-gray-300'
                      } ${reachedMax ? 'opacity-60' : ''}`}
                      onClick={() => !reachedMax && toggle(item.id)}
                    >
                      <Checkbox checked={checked} onCheckedChange={() => !reachedMax && toggle(item.id)} />
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-semibold text-slate-900">{item.name}</p>
                        <p className="truncate text-xs text-slate-600">{item.email}</p>
                      </div>
                      <Badge variant="outline">{item.currentLoad} asignaciones</Badge>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          <div className="shrink-0 space-y-4">
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div className="mb-2 flex items-center gap-2">
                <p className="font-semibold text-gray-900">Evaluaciones en progreso:</p>
                <Badge className="bg-amber-200 text-amber-900 hover:bg-amber-200">
                  {impact?.inProgressCount || 0}
                </Badge>
              </div>
              <p className="mb-1 text-sm font-semibold text-gray-900">Evaluadores actuales:</p>
              <div className="mb-3 flex flex-wrap gap-2">
                {(impact?.currentNames || []).map((name: string) => (
                  <Badge key={`current-${name}`} variant="outline" className="border-slate-400 text-slate-700">
                    {name}
                  </Badge>
                ))}
              </div>
              <p className="mb-1 text-sm font-semibold text-gray-900">Nuevos evaluadores:</p>
              <div className="flex flex-wrap gap-2">
                {(impact?.newNames || []).map((name: string) => (
                  <Badge key={`new-${name}`} className="border border-[#c9a227] bg-yellow-50 text-yellow-700 hover:bg-yellow-50">
                    {name}
                  </Badge>
                ))}
              </div>
            </div>

            <p className="text-sm text-gray-600">
              Las evaluaciones de los evaluadores removidos quedaran en estado "Cancelada". Los evaluadores que
              permanecen conservan su progreso.
            </p>

            <label className="flex items-center gap-2 text-sm font-medium text-gray-800">
              <Checkbox checked={impactConfirmed} onCheckedChange={(v) => setImpactConfirmed(Boolean(v))} />
              Confirmo que entiendo el impacto de esta reasignacion.
            </label>
          </div>
        </div>

        <DialogFooter className="shrink-0 border-t border-gray-200 bg-white px-6 py-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={reassignMutation.isPending}>
            Cancelar
          </Button>
          <Button
            className="bg-[#c9a227] text-white hover:bg-[#b8921f]"
            onClick={handleSubmit}
            disabled={
              reassignMutation.isPending
              || selectedIds.length === 0
              || selectedIds.length > 3
              || (requiresImpactConfirmation && !impactConfirmed)
            }
          >
            {reassignMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Confirmar Reasignacion
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
